import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BarChart2, Menu, Layers, Users, Settings, ClipboardList, Calendar, Bell, LogOut, ChevronLeft, ChevronRight, X, ChefHat, Ticket, MessageSquare, Smartphone } from 'lucide-react';
import { useBranches, isKdsEnabled } from './BranchesContext';
export function Sidebar({
  isOpen,
  onClose,
  isCollapsed = false,
  onToggleCollapse
}: {
  isOpen?: boolean;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    branches,
    branchId,
    setBranchId
  } = useBranches();
  const activeBranch = branches.find(b => b.id === branchId) || branches[0];
  const kdsEnabled = isKdsEnabled(activeBranch);
  const navItems = [{
    name: 'Report',
    icon: BarChart2,
    path: '/'
  }, {
    name: 'Menu',
    icon: Menu,
    path: '/menu-management'
  }, {
    name: 'Branches',
    icon: Layers,
    path: '/branches'
  }, {
    name: 'CRM',
    icon: Users,
    path: '/crm'
  }, {
    name: 'Promotions',
    icon: Ticket,
    path: '/promotions'
  }, {
    name: 'Feedback',
    icon: MessageSquare,
    path: '/feedback'
  }, {
    name: 'Settings',
    icon: Settings,
    path: '/settings'
  }, {
    name: 'Order',
    icon: ClipboardList,
    path: '/orders'
  }, {
    name: 'Bank Payments',
    icon: Smartphone,
    path: '/bank-payments'
  }, ...(kdsEnabled ? [{
    name: 'Kitchen',
    icon: ChefHat,
    path: '/kitchen'
  }, {
    name: 'Waiter',
    icon: ClipboardList,
    path: '/waiter'
  }] : [])];
  return <>
      {/* Mobile Overlay */}
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity" onClick={onClose} data-id="element-3566" />}

      {/* Sidebar Container */}
      <div className={`fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out md:relative md:translate-x-0 bg-[#0f172a] h-screen flex flex-col text-gray-300 flex-shrink-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:${isCollapsed ? 'w-[76px]' : 'w-[260px]'} ${isCollapsed ? 'md:w-[76px]' : 'md:w-[260px]'} w-[260px]`} data-id="element-3567">
        {/* Logo Area */}
        <div className={`flex items-center transition-all duration-300 ${isCollapsed ? 'md:justify-center md:p-4' : 'p-5 justify-between'}`} data-id="element-3568">
          <div className={`flex items-center gap-3 ${isCollapsed ? 'md:gap-0' : ''}`} data-id="element-3569">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-lg flex-shrink-0" data-id="element-3570">
              <span className="text-gray-900 text-xl font-bold leading-none" data-id="element-3571">
                D
              </span>
            </div>
            <div className={`leading-none overflow-hidden transition-all duration-200 ${isCollapsed ? 'md:w-0 md:opacity-0' : 'w-auto opacity-100'}`} data-id="element-3572">
              <div className="text-white font-bold text-sm tracking-wide whitespace-nowrap" data-id="element-3573">
                Demo Account
              </div>
            </div>
          </div>
          <button className="text-gray-400 hover:text-white transition-colors md:hidden" onClick={onClose} aria-label="Close menu" data-id="element-3574">
            <X size={20} data-id="element-3575" />
          </button>
        </div>

        {/* Desktop Collapse Toggle (floating on the right edge) */}
        <button onClick={onToggleCollapse} className="hidden md:flex absolute -right-3 top-7 w-6 h-6 rounded-full bg-[#1e293b] border border-gray-700 hover:bg-orange-500 hover:border-orange-500 text-yellow-500 hover:text-white items-center justify-center transition-colors shadow-md z-10" aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-3576">
          {isCollapsed ? <ChevronRight size={14} data-id="element-3577" /> : <ChevronLeft size={14} data-id="element-3578" />}
        </button>

        {/* User Info */}
        <div className={`overflow-hidden transition-all duration-200 ${isCollapsed ? 'md:max-h-0 md:py-0' : 'md:max-h-32 px-6 py-3'} ${isCollapsed ? 'px-6 py-3' : ''}`} data-id="element-3579">
          <div className="text-xs text-gray-400 mb-1 whitespace-nowrap" data-id="element-3580">
            Welcome,
          </div>
          <div className="text-white font-bold text-sm tracking-wide whitespace-nowrap" data-id="element-3581">
            QRA ADMIN
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5 whitespace-nowrap" data-id="element-3582">
            ADMIN
          </div>
        </div>

        {/* Branch Picker */}
        <div className={`overflow-hidden transition-all duration-200 ${isCollapsed ? 'md:max-h-0 md:py-0' : 'md:max-h-32 px-6 py-4'} ${isCollapsed ? 'px-6 py-4' : ''}`} data-id="element-3583">
          <div className="text-xs text-gray-400 mb-2 whitespace-nowrap flex items-center justify-between" data-id="element-3584">
            <span data-id="element-3585">Active branch</span>
            {activeBranch && <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider" data-id="element-3586">
                <span className={`w-1.5 h-1.5 rounded-full ${activeBranch.active ? 'bg-emerald-400' : 'bg-amber-400'}`} data-id="element-3587" />
                <span className={activeBranch.active ? 'text-emerald-400' : 'text-amber-400'} data-id="element-3588">
                  {activeBranch.active ? 'Open' : 'Paused'}
                </span>
              </span>}
          </div>
          {branches.length <= 1 ? <div className="w-full bg-[#1e293b] border border-gray-700 rounded-md text-sm text-white px-3 py-2 truncate" data-id="element-3589">
              {activeBranch?.name || 'No branch'}
            </div> : <div className="relative" data-id="element-3590">
              <select value={branchId} onChange={e => setBranchId(e.target.value)} className="w-full bg-[#1e293b] border border-gray-700 rounded-md text-sm text-white px-3 py-2 outline-none focus:border-gray-500 appearance-none cursor-pointer pr-8" data-id="element-3591">
                {branches.map(b => <option key={b.id} value={b.id} data-id="element-3592">
                    {b.name}
                    {!b.active ? ' · paused' : ''}
                  </option>)}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" data-id="element-3593">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3594">
                  <path d="m6 9 6 6 6-6" data-id="element-3595" />
                </svg>
              </div>
            </div>}
        </div>

        {/* Nav Items */}
        <div className={`flex-1 overflow-y-auto overflow-x-hidden py-2 ${isCollapsed ? 'md:mt-2' : 'mt-2'}`} data-id="element-3596">
          {navItems.map(item => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return <button key={item.name} title={isCollapsed ? item.name : undefined} onClick={() => {
            navigate(item.path);
            onClose?.();
          }} className={`group relative w-full flex items-center text-sm font-medium transition-colors ${isCollapsed ? 'md:justify-center md:px-0 md:py-3.5 px-6 py-3.5 gap-4' : 'gap-4 px-6 py-3.5'} ${active ? 'bg-[#3b2a1c] text-orange-500 border-l-4 border-orange-500' : 'text-gray-400 hover:bg-[#1e293b] hover:text-gray-200 border-l-4 border-transparent'}`} data-id="element-3597">
                <Icon size={20} className={`flex-shrink-0 ${active ? 'text-orange-500' : 'text-yellow-500'}`} data-id="element-3598" />
                <span className={`whitespace-nowrap transition-all duration-200 ${isCollapsed ? 'md:hidden inline' : 'inline'}`} data-id="element-3599">
                  {item.name}
                </span>

                {/* Tooltip when collapsed (desktop only) */}
                {isCollapsed && <span className="hidden md:group-hover:block absolute left-full ml-3 px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium whitespace-nowrap shadow-lg z-50 pointer-events-none" data-id="element-3600">
                    {item.name}
                  </span>}
              </button>;
        })}
        </div>

        {/* Bottom Buttons */}
        <div className={`space-y-3 ${isCollapsed ? 'md:p-3 p-6' : 'p-6'}`} data-id="element-3601">
          <button title={isCollapsed ? 'Notifications' : undefined} className={`group relative w-full flex items-center rounded-lg border border-red-900/60 text-red-400 hover:bg-red-900/20 transition-colors text-sm font-medium ${isCollapsed ? 'md:justify-center md:px-2 md:py-2.5 gap-3 px-4 py-2.5' : 'gap-3 px-4 py-2.5'}`} data-id="element-3602">
            <Bell size={18} className="flex-shrink-0" data-id="element-3603" />
            <span className={`whitespace-nowrap ${isCollapsed ? 'md:hidden inline' : 'inline'}`} data-id="element-3604">
              Notifications
            </span>
            {isCollapsed && <span className="hidden md:group-hover:block absolute left-full ml-3 px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium whitespace-nowrap shadow-lg z-50 pointer-events-none" data-id="element-3605">
                Notifications
              </span>}
          </button>
          <button title={isCollapsed ? 'Logout' : undefined} className={`group relative w-full flex items-center rounded-lg border border-red-900/60 text-red-400 hover:bg-red-900/20 transition-colors text-sm font-medium ${isCollapsed ? 'md:justify-center md:px-2 md:py-2.5 gap-3 px-4 py-2.5' : 'gap-3 px-4 py-2.5'}`} data-id="element-3606">
            <LogOut size={18} className="flex-shrink-0" data-id="element-3607" />
            <span className={`whitespace-nowrap ${isCollapsed ? 'md:hidden inline' : 'inline'}`} data-id="element-3608">
              Logout
            </span>
            {isCollapsed && <span className="hidden md:group-hover:block absolute left-full ml-3 px-2.5 py-1.5 rounded-md bg-gray-900 text-white text-xs font-medium whitespace-nowrap shadow-lg z-50 pointer-events-none" data-id="element-3609">
                Logout
              </span>}
          </button>
        </div>
      </div>
    </>;
}